import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {BottomSheetSelectType, ButtonsType, SelectItemType, WebviewUtils} from '@shared/webviewUtils';

export interface OpenAlertProps {
  title: string;
  message?: string;
  okText?: string;
}

export interface OpenConfirmProps {
  title: string;
  message?: string;
  isVerticalButton?: boolean;
  okText?: string;
  cancelText?: string;
}

interface BottomSheetBase {
  items: Array<SelectItemType>;
  selectedIndexes?: Array<number>;
  title?: string;
  message?: string;
  isCancellable?: boolean;
}

interface BottomSheetSingleSelect extends BottomSheetBase {
  isMultiple: false;
  buttons?: Array<ButtonsType>;
}

interface BottomSheetMultiSelect extends BottomSheetBase {
  isMultiple: true;
  buttons: [ButtonsType, ...Array<ButtonsType>];
}

export type OpenBottomSheetProps = BottomSheetSingleSelect | BottomSheetMultiSelect;

export default function useCcWebviewUtilsHelper() {
  const {t} = useInferableTranslation();

  async function openAlert({title, message = '', okText = t('T10114')}: OpenAlertProps) {
    await WebviewUtils.openCustomAlert({
      title,
      message,
      buttons: [{text: okText, style: 'PRIMARY', actionType: 'DEFAULT'}],
    });
  }

  async function openConfirm({
    title,
    message = '',
    isVerticalButton = true,
    okText = t('T10114'),
    cancelText = t('T10017'),
  }: OpenConfirmProps) {
    const selectedIndex = await WebviewUtils.openCustomAlert({
      title,
      message,
      isVerticalButton,
      buttons: [
        {text: okText, style: 'PRIMARY', actionType: 'DEFAULT'},
        {text: cancelText, style: 'BASIC', actionType: 'CANCEL'},
      ],
    });

    return selectedIndex === 0;
  }

  async function openBottomSheet({
    title = '',
    message = '',
    isMultiple = false,
    selectedIndexes = [],
    items,
    buttons = [],
    isCancellable = true,
  }: OpenBottomSheetProps) {
    const result = await WebviewUtils.openBottomSheet({
      type: isMultiple ? BottomSheetSelectType.MULTIPLE_SELECT : BottomSheetSelectType.SINGLE_SELECT,
      title,
      message,
      selectedIndexes,
      items,
      buttons,
      isCancellable,
    });

    if (!result || !result.selectedIndexes) {
      return null;
    }

    return result;
  }

  function backOrClose() {
    WebviewUtils.historyBack();
  }

  function openNewStackWebView(fullPath: string) {
    WebviewUtils.openNewWebView({
      url: window.location.origin + fullPath,
      isModal: true,
      isNavigationBar: true,
      isToolBar: false,
    });
  }

  function openDINewStackWebView(fullPath: string) {
    WebviewUtils.openNewWebView({
      url: window.location.origin + fullPath,
      isModal: true,
      isNavigationBar: false,
      isToolBar: false,
    });
  }

  function openInAppBrowser(url: string) {
    WebviewUtils.openNewWebView({
      url,
      isNavigationBar: false,
    });
  }

  return {
    openAlert,
    openConfirm,
    openBottomSheet,
    backOrClose,
    openNewStackWebView,
    openInAppBrowser,
    openDINewStackWebView,
  };
}
